/**
 * compiled-query-types.ts — TypeScript interfaces for the compiled query system
 * Part of PRI-291: Input Pill Compiled Query
 */

export interface CompiledQuery {
  query_type: 'object' | 'action' | 'compound' | 'attribute'
  search_terms: string[]
  segment_targets: string[]
  subjects: string[]
  actions: string[]
  connective: 'and' | 'or' | null
  prompt_intent: 'presence' | 'absence' | 'count' | 'action'
              | 'attribute' | 'location' | 'state' | 'temporal' | null
  absence_terms: string[]
  count_terms: string[]
  original_prompt: string

  // v3 fields
  conditions: Condition[]
  quantifier: { operator: '>' | '>=' | '<' | '=='; value: number } | null
  unassessable_components: string[]
  response_framing: 'yes_no' | 'description' | 'count'

  // Optional extended fields
  self_reference?: boolean
  display_map?: Record<string, string>
  attribute_property?: string
  attribute_value?: string
}

export interface Condition {
  /** Logical category of the condition.
   *  PRI-271 fix: extended beyond 'presence' | 'action' so per-lane LaneType
   *  (LOCATION, ATTRIBUTE, etc.) survives the rebuildQuery() -> deriveLanes()
   *  round-trip. Older 'presence' | 'action' values continue to work. */
  type: 'presence' | 'action' | 'subject' | 'object' | 'location' | 'attribute' | 'state' | 'threshold' | 'count' | 'temporal'
  target: string
  negated: boolean
  /** Connective preceding this condition. Optional, only set when user edited a connective. */
  connective?: ConnectiveValue
}

export type ParseState = 'idle' | 'parsing' | 'lanes_visible' | 'edit' | 'error'

export type LaneType =
  | 'SUBJECT'
  | 'OBJECT'
  | 'ACTION'
  | 'PRESENCE'
  | 'WHAT TO COUNT'
  | 'THRESHOLD'
  | 'ATTRIBUTE'
  | 'LOCATION'
  | 'STATE'
  | 'TEMPORAL'

export type ConnectiveValue = 'and' | 'or' | 'and_not'

export interface Lane {
  id: string
  type: LaneType
  target: string
  negated: boolean
  unassessable?: string[]
  /** Connective preceding this lane (ignored on the first lane). Per-gap value. */
  connective?: ConnectiveValue
}

// ── Utility functions ─────────────────────────────────────────────────────────

/** Maps a CompiledQuery to an array of swim lanes per spec §2 "Lane Derivation Rules" */
export function deriveLanes(query: CompiledQuery): Lane[] {
  const lanes: Lane[] = []
  let idCounter = 0
  const nextId = () => `lane-${idCounter++}`
  const unassessable = query.unassessable_components ?? []

  // conditions[] is authoritative when populated
  if (query.conditions && query.conditions.length > 0) {
    for (const condition of query.conditions) {
      // PRI-271 fix (bug 1): preserve full LaneType through round-trip.
      // Previously this mapped only 'action' -> ACTION and everything else
      // -> SUBJECT, which dropped LOCATION/ATTRIBUTE/etc. on every re-derive.
      const type: LaneType = mapConditionTypeToLane(condition.type)
      lanes.push({
        connective: condition.connective,
        id: nextId(),
        type,
        target: condition.target,
        negated: condition.negated,
        unassessable: unassessable.filter(u => condition.target.includes(u)),
      })
    }
    // PRI-271 fix (bug 3): if any condition supplied its own connective, do NOT
    // overwrite per-lane connectives with the global query.connective. Only
    // seed missing ones from the global value.
    const baseConnective: ConnectiveValue = query.connective === 'or' ? 'or' : 'and'
    for (let i = 1; i < lanes.length; i++) {
      if (!lanes[i].connective) {
        lanes[i].connective = lanes[i].negated ? 'and_not' : baseConnective
      }
    }
    return lanes
  }

  const intent = query.prompt_intent

  switch (intent) {
    case 'presence':
      for (const subject of (query.subjects ?? [])) {
        lanes.push({
          id: nextId(), type: 'SUBJECT', target: subject, negated: false,
          unassessable: unassessable.filter(u => subject.includes(u)),
        })
      }
      break

    case 'absence':
      for (const term of (query.absence_terms ?? [])) {
        lanes.push({
          id: nextId(), type: 'SUBJECT', target: term, negated: true,
          unassessable: unassessable.filter(u => term.includes(u)),
        })
      }
      break

    case 'count': {
      for (const term of (query.count_terms ?? [])) {
        lanes.push({ id: nextId(), type: 'WHAT TO COUNT', target: term, negated: false })
      }
      if (query.quantifier) {
        const opMap: Record<string, string> = {
          '>': 'more than', '>=': 'at least', '<': 'less than', '==': 'exactly',
        }
        const opText = opMap[query.quantifier.operator] ?? query.quantifier.operator
        lanes.push({
          id: nextId(), type: 'THRESHOLD',
          target: `${opText} ${query.quantifier.value}`, negated: false,
        })
      }
      break
    }

    case 'action':
      for (const subject of (query.subjects ?? [])) {
        lanes.push({
          id: nextId(), type: 'SUBJECT', target: subject, negated: false,
          unassessable: unassessable.filter(u => subject.includes(u)),
        })
      }
      for (const action of (query.actions ?? [])) {
        lanes.push({
          id: nextId(), type: 'ACTION', target: action, negated: false,
          unassessable: unassessable.filter(u => action.includes(u)),
        })
      }
      break

    case 'attribute':
      for (const subject of (query.subjects ?? [])) {
        lanes.push({ id: nextId(), type: 'SUBJECT', target: subject, negated: false })
      }
      if (query.attribute_property) {
        const attrTarget = query.attribute_value
          ? `${query.attribute_property}: ${query.attribute_value}`
          : `${query.attribute_property}: ?`
        lanes.push({ id: nextId(), type: 'ATTRIBUTE', target: attrTarget, negated: false })
      }
      break

    case 'location':
      for (const subject of (query.subjects ?? [])) {
        lanes.push({ id: nextId(), type: 'SUBJECT', target: subject, negated: false })
      }
      lanes.push({ id: nextId(), type: 'LOCATION', target: '', negated: false })
      break

    case 'state':
      for (const subject of (query.subjects ?? [])) {
        lanes.push({ id: nextId(), type: 'SUBJECT', target: subject, negated: false })
      }
      if (query.attribute_property) {
        const stateTarget = query.attribute_value
          ? `${query.attribute_property}: ${query.attribute_value}`
          : query.attribute_property
        lanes.push({ id: nextId(), type: 'STATE', target: stateTarget, negated: false })
      }
      break

    case 'temporal':
      lanes.push({ id: nextId(), type: 'TEMPORAL', target: '', negated: false })
      break
  }

  // Seed per-lane connectives from the global query.connective. After this,
  // each gap is independently editable (per spec §7: "Each gap has its own
  // independent connective"). Negated lanes default to 'and_not'.
  const baseConnective: ConnectiveValue = query.connective === 'or' ? 'or' : 'and'
  for (let i = 1; i < lanes.length; i++) {
    lanes[i].connective = lanes[i].negated ? 'and_not' : baseConnective
  }

  return lanes
}

/** Maps a Condition.type to the corresponding LaneType (PRI-271). */
function mapConditionTypeToLane(t: Condition['type']): LaneType {
  switch (t) {
    case 'action': return 'ACTION'
    case 'location': return 'LOCATION'
    case 'attribute': return 'ATTRIBUTE'
    case 'state': return 'STATE'
    case 'threshold': return 'THRESHOLD'
    case 'count': return 'WHAT TO COUNT'
    case 'object': return 'OBJECT'
    case 'temporal': return 'TEMPORAL'
    case 'presence': return 'SUBJECT' // historical alias
    case 'subject': return 'SUBJECT'
    default: return 'SUBJECT'
  }
}

/** Maps a LaneType back to a Condition.type for round-trip (PRI-271). */
export function mapLaneTypeToCondition(t: LaneType): Condition['type'] {
  switch (t) {
    case 'ACTION': return 'action'
    case 'LOCATION': return 'location'
    case 'ATTRIBUTE': return 'attribute'
    case 'STATE': return 'state'
    case 'THRESHOLD': return 'threshold'
    case 'WHAT TO COUNT': return 'count'
    case 'OBJECT': return 'object'
    case 'TEMPORAL': return 'temporal'
    case 'PRESENCE':
    case 'SUBJECT':
    default: return 'presence'
  }
}

/** Returns one connective value per gap between adjacent lanes.
 *  Per spec §7: each gap is independent. Prefers `lane.connective` when present,
 *  falls back to the query-level connective for legacy lanes. */
export function deriveConnectives(lanes: Lane[], query: CompiledQuery): ConnectiveValue[] {
  if (lanes.length <= 1) return []
  const fallback: ConnectiveValue = query.connective === 'or' ? 'or' : 'and'
  const connectives: ConnectiveValue[] = []
  for (let i = 1; i < lanes.length; i++) {
    const nextLane = lanes[i]
    if (nextLane.connective) {
      connectives.push(nextLane.connective)
    } else if (nextLane.negated) {
      connectives.push('and_not')
    } else {
      connectives.push(fallback)
    }
  }
  return connectives
}

/** Generates the preview strip text per spec §9 */
export function generatePreviewText(query: CompiledQuery): string {
  const framing = query.response_framing
  const intent = query.prompt_intent
  const subjects = (query.subjects ?? []).join(', ') || 'subjects'
  const actions = (query.actions ?? []).join(', ') || 'the action'
  const absence = (query.absence_terms ?? []).join(', ') || 'subjects'
  const counts = (query.count_terms ?? []).join(', ') || 'items'

  if (framing === 'yes_no') {
    if (intent === 'presence') return `Darwin will respond yes or no to whether ${subjects} are present`
    if (intent === 'absence') return `Darwin will respond yes or no to whether ${absence} are absent`
    if (intent === 'action') return `Darwin will respond yes or no to whether ${subjects} are ${actions}`
    if (intent === 'state') return `Darwin will respond yes or no to whether ${subjects} are ${query.attribute_value || 'in the state'}`
  }
  if (framing === 'count' && intent === 'count') {
    return `Darwin will respond with a count of ${counts}`
  }
  if (framing === 'description') {
    if (intent === 'attribute') return `Darwin will respond describing the ${query.attribute_property || 'attribute'} of ${subjects}`
    if (intent === 'location') return `Darwin will respond describing where ${subjects} are located`
    if (intent === 'temporal') return `Darwin will respond describing what has changed`
  }
  return `Darwin will respond to your query`
}
